// @flow
// $FlowFixMe - Cannot import useEffect because there is no useEffect export in react.
import React, { useEffect, useState, useRef } from 'react';
import { colors } from 'bench-style-constants';
import {
  SidebarSection,
  Text,
  TextInputHookForm,
  Checkbox,
  checkboxStates,
} from 'bench-internal-theme';
import { useFormContext } from 'react-hook-form';
import InstitutionSelect from './InstitutionSelect';
import ProductTypeSelect from './ProductTypeSelect';
import type { LedgerAccount } from '../types';
import { FormGroup } from '../FormGroups';
import { Icon } from 'bench-internal-theme';
import { Tooltip } from 'client-web-app/ui';
import styled from 'styled-components';

const TooltipContent = styled.div`
  width: 88px;
`;

const InputIconFormGroup = styled.div`
  align-items: center;
  display: grid;
  gap: 4px;
  grid-template-columns: 130px 132px 32px;
`;

const IconWrapper = styled.div`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

type FunctionalAccountDetailsSectionProps = {
  ledgerAccount: ?LedgerAccount,
  isSubmitting: boolean,
  isTemporary: boolean,
};

const LastDigitProperties = {
  lastFour: {
    title: 'Last Four Digits',
    length: 4,
  },
  lastFive: {
    title: 'Last Five Digits',
    length: 5,
  },
};

const InstitutionsWithLastFiveDigits = ['American Express'];

function isAccountLastDigitsInAccountName(accountName: string, accountLastDigits: string): boolean {
  const sanitizedAccountName = accountName.replace(/[_\-\s]/g, '');
  return sanitizedAccountName.includes(accountLastDigits);
}

const FunctionalAccountDetailsSection = ({
  isSubmitting,
  ledgerAccount,
  isTemporary,
}: FunctionalAccountDetailsSectionProps) => {
  const { watch, setValue } = useFormContext();
  const [lastDigitProperties, setLastDigitProperties] = useState(LastDigitProperties.lastFour);

  const productType = watch('productType', undefined);
  const categoryId = watch('categoryId', undefined);
  const institution = watch('institution', undefined);
  const accountLastDigits = watch('accountLastDigits', '');
  const accountName = watch('name', '');
  const lastFourDigitsChecked = watch('accountLastDigitsCheckbox');

  const institutionRef = useRef();

  const formatLedgerName = (): string => {
    // $FlowFixMe - Flow does not yet support method or property calls in optional chains.
    const productName = productType?.label?.split(' (')[0];
    // $FlowFixMe - Flow does not yet support method or property calls in optional chains.
    const institutionName = institution?.label?.split('-')[0].trim();

    let name = '';
    if (institutionName && productName && accountLastDigits) {
      name = `${institutionName} - ${productName} - ${accountLastDigits}`;
    }
    if (institutionName && productName && !accountLastDigits) {
      name = `${institutionName} - ${productName}`;
    }
    if (institutionName && !productName && !accountLastDigits) {
      name = institutionName;
    }
    if (categoryId.value === '9') name = `${name} (Personal Account)`;

    return name;
  };

  const updateName = () => {
    const name = formatLedgerName();
    // If category is Equity and Product Type is Checking (Business Personal Crossover),
    // Savings (Business Personal Crossover) or Credit Card (Business Personal Crossover) add prefix
    const addPrefix =
      productType &&
      categoryId &&
      categoryId.value === '5' &&
      ['10', '11', '12'].includes(productType.metaData.productTypeId);

    const nameWithPrefix = addPrefix ? `P/B - ${name}` : name;

    setValue('name', nameWithPrefix);
  };

  useEffect(() => {
    // When categoryId is changed, clear productType because the available options are different
    setValue('productType', null);
  }, [categoryId, setValue]);

  useEffect(() => {
    // When institution is cleared, clear productType because the available options are different
    if (!institution) {
      setValue('productType', null);
      return;
    }

    // Require five digits only if the financial institution is "American Express" and
    // the product type is "Credit Card." if not, require four digits
    if (
      InstitutionsWithLastFiveDigits.includes(institution?.label) &&
      productType?.label === 'Credit Card'
    ) {
      setLastDigitProperties(LastDigitProperties.lastFive);
      if (accountLastDigits.length > LastDigitProperties.lastFive.length) {
        setValue(
          'accountLastDigits',
          accountLastDigits.slice(0, LastDigitProperties.lastFive.length),
        );
      }
    } else {
      setLastDigitProperties(LastDigitProperties.lastFour);
      if (accountLastDigits.length > LastDigitProperties.lastFour.length) {
        setValue(
          'accountLastDigits',
          accountLastDigits.slice(0, LastDigitProperties.lastFour.length),
        );
      }
    }
  }, [setValue, institution, productType, accountLastDigits]);

  useEffect(() => {
    const prevInstitution = institutionRef.current;
    // When institution is changed between American Express and other types, reset the last digits
    if (
      (prevInstitution &&
        prevInstitution.label === 'American Express' &&
        institution &&
        institution.label !== 'American Express') ||
      (prevInstitution &&
        prevInstitution.label !== 'American Express' &&
        institution &&
        institution.label === 'American Express')
    ) {
      setValue('accountLastDigits', null);
    }
    institutionRef.current = institution;
  }, [institution, setValue]);

  return (
    <SidebarSection padding="0 24px 24px 24px">
      <FormGroup>
        <Text color={colors.cloud} styleType="small" uppercase>
          Institution
        </Text>
        <InstitutionSelect
          ledgerAccount={ledgerAccount}
          isSubmitting={isSubmitting}
          isTemporary={isTemporary}
        />
        <Text color={colors.cloud} styleType="small" uppercase>
          Product Type
        </Text>
        <ProductTypeSelect
          isSubmitting={isSubmitting}
          ledgerAccount={ledgerAccount}
          isTemporary={isTemporary}
        />
      </FormGroup>
      <InputIconFormGroup>
        <Text color={colors.cloud} styleType="small" uppercase>
          {lastDigitProperties.title}
        </Text>
        <TextInputHookForm
          aria-label="Last Digits"
          disabled={isSubmitting || lastFourDigitsChecked}
          name="accountLastDigits"
          maxLength={`${lastDigitProperties.length}`}
          type="text"
          pattern="\d*"
          rules={{
            minLength: {
              value: lastDigitProperties.length,
              message: `Must be ${lastDigitProperties.length} digits`,
            },
            required: {
              value: !lastFourDigitsChecked,
              message: 'Account last digits are required. Check box if not applicable.',
            },
            validate: {
              lastFourDigitsChecked: value => lastFourDigitsChecked || value !== '',
              isAccountLastDigitsInAccountName: value =>
                lastFourDigitsChecked || isAccountLastDigitsInAccountName(accountName, value)
                  ? true
                  : 'Account last digits included in account name must match entered value.',
            },
          }}
          defaultValue={ledgerAccount?.accountLastFourDigits}
        />
        <IconWrapper onClick={updateName}>
          {institution?.value && (
            <Tooltip
              placement="bottom"
              tooltipContent={<TooltipContent>Update name with account details</TooltipContent>}
              styleType="light"
            >
              <Icon
                aria-label="Update Name"
                color={colors.cloud}
                hoverColor={colors.silt}
                icon="refresh"
                size="small"
                cursor="pointer"
              />
            </Tooltip>
          )}
        </IconWrapper>
        <Text color={colors.cloud} styleType="small" uppercase>
          Last digits not applicable
        </Text>
        <Checkbox
          aria-label="Account last digits not applicable"
          disabled={isSubmitting}
          checkboxState={lastFourDigitsChecked ? checkboxStates.CHECKED : checkboxStates.UNCHECKED}
          onClick={() => {
            const newCheckedState = !lastFourDigitsChecked;
            setValue('accountLastDigitsCheckbox', newCheckedState);
            if (newCheckedState) {
              setValue('accountLastDigits', '');
            }
          }}
        />
      </InputIconFormGroup>
    </SidebarSection>
  );
};

export default FunctionalAccountDetailsSection;
